// CustomValueEditor.tsx
import React, { useEffect, useRef, useState } from "react";
import Select, { DropdownIndicatorProps, components } from "react-select";
import { theme } from "../../../Customization/Theme";
import { getReactSelectCurrentValue } from "../utils/utils";
import useDebounce from "../../../Hooks/useDebounce";
import { useAppDispatch } from "../../../Store";
import { QueryBuilderActions } from "../../../Store/Slices/QueryBuilder/QueryBuilder.actions";

interface SearchInputProps {
  onOptionSelect: (option: any) => void;
  [props: string]: any;
}

export const CustomQueryValueSearchField: React.FC<SearchInputProps> = ({
  onOptionSelect,
  ...props
}) => {
  const dispatch = useAppDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [value, setValue] = React.useState<any>(null);
  // Options are held per instance, not read back from the slice: every rule on a
  // field shares one cache slot there, so rules on the same field overwrite each
  // other's search results and only the last response survives.
  const [options, setOptions] = useState<any[]>([]);
  const latestRequestId = useRef(0);

  const debouncedSearchTerm = useDebounce<string>(searchTerm, 1000);
  const requestSearchOptions = (id: string, searchTerm: string) => {
    const payload = {
      id,
      search: searchTerm,
      automation: true,
    };
    const requestId = ++latestRequestId.current;
    dispatch(
      QueryBuilderActions.getQueryBuilderFieldDynamicOptions(payload),
    ).then((res: any) => {
      if (requestId !== latestRequestId.current) return;
      const nextOptions = res?.payload?.data?.data?.options;
      if (Array.isArray(nextOptions)) setOptions(nextOptions);
    });
  };

  useEffect(() => {
    if (props?.fieldData?.id) {
      if (debouncedSearchTerm?.length > 1) {
        requestSearchOptions(props?.fieldData?.id, debouncedSearchTerm);
      }
    }
  }, [debouncedSearchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      (options?.length > 0 &&
        !value &&
        props?.type === "search" &&
        props?.value) ||
      (options?.length > 0 &&
        !value &&
        props?.type === "autocomplete" &&
        props?.value)
    ) {
      const opt = getReactSelectCurrentValue(
        options,
        props?.value,
        "value",
        false,
      );
      setValue(opt);
    }
  }, [options]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (props?.fieldData?.id && props?.value) {
      setSearchTerm(props?.value);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      props?.value &&
      (props?.type === "search" || props?.type === "autocomplete")
    ) {
      const opt = getReactSelectCurrentValue(
        options,
        props?.value,
        "label",
        true,
      );
      setValue(opt);
    } else {
      setValue(null);
    }
  }, [props?.value]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFieldSelectChange = (selectedValue: any) => {
    onOptionSelect(selectedValue);
  };

  const handleFieldTextChange = (val: string) => {
    setSearchTerm(val);
  };

  let customName: string = "CustomQueryValueField";

  if (props?.type === "search" || props?.type === "autocomplete") {
    return (
      <Select
        id={customName}
        options={options?.length > 0 ? options : props.values ?? []}
        value={value}
        onChange={(e: any) => handleFieldSelectChange(e)}
        onInputChange={(inputValue: any) => handleFieldTextChange(inputValue)}
        placeholder={"Search..."}
        getOptionLabel={(option: any) =>
          option?.label || option?.name || option?.value || option?.toString()
        }
        isOptionSelected={(option: any) =>
          option?.value || option?.name || option?.label
        }
        isOptionDisabled={(option: any) => option?.disabled === true}
        isMulti={false}
        isSearchable={true}
        isClearable={true}
        blurInputOnSelect={false}
        noOptionsMessage={() => "No Options "}
        styles={customStyles}
        menuPosition="fixed"
        components={{ DropdownIndicator }}
        className="react-select-container"
        classNamePrefix="react-select"
      />
    );
  }

  return null;
};

const DropdownIndicator: React.FC<DropdownIndicatorProps> = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <svg
        height="20"
        width="20"
        viewBox="0 0 20 20"
        aria-hidden="true"
        focusable="false"
        className="css-tj5bde-Svg"
      >
        <path d="M7 10l5 5 5-5z"></path>
      </svg>
    </components.DropdownIndicator>
  );
};

const customStyles = {
  control: (base: any, state: any) => ({
    ...base,
    backgroundColor: "rgb(245, 246, 248) !important",
    minHeight: "32px !important",
    maxHeight: "32px !important",
    minWidth: "180px !important",
    maxWidth: "180px !important",
    boxShadow: "none !important",
    paddingBottom: "0px",
    borderColor: state?.isFocused ? "transparent" : "transparent",
    "&:hover": {
      borderColor: state?.isFocused ? "transparent" : "transparent",
    },
  }),
  menu: (base: any) => ({
    ...base,
    // override border radius to match the box
    borderRadius: 2,
    // kill the gap
    marginTop: 4,
    backgroundColor: "rgb(245, 246, 248) !important",
  }),

  menuList: (base: any) => ({
    ...base,
    // kill the white space on first and last option
    backgroundColor: "white !important",
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: "transaparent !important",
    color: theme.palette.default.darkGrey,
    fontSize: "14px",

    "&:hover": {
      backgroundColor: "rgb(245, 246, 248) !important",
    },
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base: any) => ({
    ...base,
    color: "rgba(0, 0, 0, 0.54) !important",
    // display: "flex",
    justifyContent: "center",
    alignItems: "flex-end",
    height: "16px !important",
    padding: "0px !important",
    marginRight: "8px !important",
    svg: {
      fill: "rgba(0, 0, 0, 0.54) !important",
    },
    display: "none",
  }),

  ValueEditor: (base: any) => ({
    ...base,
    backgroundColor: "red !important",
    fontSize: "23px",
  }),
  ".react-select__single-value": {
    backgroundColor: "red !important",
    fontSize: "19px",
  },
};
